const unavailableRevenue = Object.freeze({kind: "unavailable", entries: []});

function finiteAmount(value) {
    if (value === null || value === undefined || value === "") return null;
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : null;
}

/** Sums only observed values; a missing value is never interpreted as zero. */
export function summarizeCoveredMetric(rows, field, coverage) {
    const sourceRows = Array.isArray(rows) ? rows : [];
    if (coverage?.available !== true) {
        return {value: null, state: "unavailable", observedCount: 0, missingCount: sourceRows.length};
    }

    let value = 0;
    let observedCount = 0;
    sourceRows.forEach((row) => {
        const amount = finiteAmount(row?.[field]);
        if (amount === null) return;
        value += amount;
        observedCount += 1;
    });

    if (observedCount === 0) {
        return {value: null, state: "unavailable", observedCount: 0, missingCount: sourceRows.length};
    }

    const missingCount = sourceRows.length - observedCount;
    const complete = coverage.complete === true && missingCount === 0;
    return {value, state: complete ? "complete" : "observed", observedCount, missingCount};
}

/**
 * Produces a safe display model for GA4 export purchase revenue. A single
 * currency total is returned only when every input is explicitly AVAILABLE
 * and uses the same currency. Otherwise the UI can show a labelled breakdown.
 */
export function summarizeCanonicalPurchaseRevenue(reports) {
    const sourceReports = Array.isArray(reports) ? reports : [];
    if (sourceReports.length === 0) return unavailableRevenue;

    const entries = new Map();
    let everyReportAvailable = true;
    let everyReportRepresented = true;
    let hasMissingPurchaseRevenue = false;
    for (const report of sourceReports) {
        const missingPurchaseRevenueCount = finiteAmount(report?.purchaseRevenueMissingCount);
        const reportHasMissingPurchaseRevenue = missingPurchaseRevenueCount !== null && missingPurchaseRevenueCount > 0;
        hasMissingPurchaseRevenue = hasMissingPurchaseRevenue || reportHasMissingPurchaseRevenue;
        const status = report?.purchaseRevenueAvailability;
        if (!reportHasMissingPurchaseRevenue && status === "AVAILABLE" && report?.purchaseRevenueCurrency) {
            const amount = finiteAmount(report.canonicalPurchaseRevenue);
            if (amount !== null) {
                const currency = report.purchaseRevenueCurrency;
                const current = entries.get(currency) ?? {currency, amount: 0};
                entries.set(currency, {...current, amount: current.amount + amount});
                continue;
            }
        }

        everyReportAvailable = false;
        const groups = Array.isArray(report?.purchaseRevenueByCurrency) ? report.purchaseRevenueByCurrency : [];
        let reportRepresented = false;
        for (const group of groups) {
            const amount = finiteAmount(group?.purchaseRevenue);
            if (amount === null) continue;
            reportRepresented = true;
            const currency = group.currency || null;
            const key = currency ?? "UNKNOWN_CURRENCY";
            const current = entries.get(key) ?? {currency, amount: 0};
            entries.set(key, {...current, amount: current.amount + amount});
        }
        if (!reportRepresented) everyReportRepresented = false;
    }

    if (everyReportAvailable && entries.size === 1) {
        const [currency, entry] = entries.entries().next().value;
        return {kind: "single", currency, amount: entry.amount, entries: [entry], partial: false};
    }

    const breakdown = [...entries.values()];
    if (breakdown.length === 0) return unavailableRevenue;
    return {kind: "breakdown", entries: breakdown, partial: !everyReportRepresented || hasMissingPurchaseRevenue};
}

/** One revenue-bearing GA4 row follows the same availability and currency rules. */
export function canonicalPurchaseRevenueRow(row) {
    return summarizeCanonicalPurchaseRevenue(row ? [row] : []);
}

export function summarizeSourceCoverage(coverage) {
    if (!coverage || coverage.available !== true) return {state: "unavailable", observedDays: null, missingDays: null};
    const observedDays = Array.isArray(coverage.observedDays) ? coverage.observedDays.length : 0;
    const missingDays = Array.isArray(coverage.missingDays) ? coverage.missingDays.length : null;
    if (coverage.complete === true && missingDays === 0) return {state: "complete", observedDays, missingDays};
    if (coverage.complete === false) return {state: "incomplete", observedDays, missingDays};
    return {state: "unknown", observedDays, missingDays};
}

export function commercialContextsCoverageComplete(contexts) {
    const rows = Array.isArray(contexts) ? contexts : [];
    if (rows.length === 0) return null;
    const flags = rows.map((row) => row?.coverageComplete);
    if (flags.some((value) => value === false)) return false;
    return flags.every((value) => value === true) ? true : null;
}
